import { z } from 'zod';
import { CHAT_MAX_LENGTH } from './game/constants';

/** Client-generated idempotency id (uuid or nanoid-like). */
const clientId = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, 'invalid client id');
const uuid = z.string().uuid();
const choice = z.enum(['KISS', 'REFUSE']);
export const localeSchema = z.enum(['en', 'ru', 'uz', 'az', 'tr']);
export const genderSchema = z.enum(['MALE', 'FEMALE']);

// ---------------------------------------------------------------------------------------------
// Socket.IO: client → server payloads (namespace /game). Every event is acknowledged with Ack<T>.
// ---------------------------------------------------------------------------------------------

export const socketSchemas = {
  'room.quickJoin': z.object({}).strict(),
  'room.join': z.object({ roomId: z.string().regex(/^[A-Za-z0-9_-]{6,40}$/) }).strict(),
  'room.leave': z.object({}).strict(),
  'room.change': z.object({}).strict(),
  'room.sync': z.object({}).strict(),
  'turn.spin': z.object({ turnId: z.string().max(40) }).strict(),
  'round.decide': z.object({ roundId: z.string().max(40), choice }).strict(),
  'chat.send': z
    .object({
      clientMsgId: clientId,
      text: z
        .string()
        .min(1)
        .max(CHAT_MAX_LENGTH * 2),
    })
    .strict(),
  'gift.send': z.object({ toUserId: uuid, giftTypeId: uuid, clientTxId: clientId }).strict(),
  'dj.order': z
    .object({
      url: z.string().min(5).max(300),
      clientTxId: clientId,
      /** Dedicate the song to a player at the table (the "song" / "music video" gifts). */
      toUserId: uuid.optional(),
      video: z.boolean().optional(),
    })
    .strict(),
  'sticker.send': z
    .object({
      pack: z.string().regex(/^[a-z0-9_]{2,32}$/),
      index: z.number().int().min(0).max(63),
      clientTxId: clientId,
    })
    .strict(),
  'live.join': z.object({ streamId: uuid }).strict(),
  'live.leave': z.object({ streamId: uuid }).strict(),
  'live.chat': z.object({ streamId: uuid, text: z.string().min(1).max(200), clientMsgId: clientId }).strict(),
  'live.like': z.object({ streamId: uuid }).strict(),
  'live.ping': z.object({ streamId: uuid }).strict(),
  'bottle.change': z.object({ skin: z.string().regex(/^[a-z0-9_]{2,32}$/), clientTxId: clientId }).strict(),
} as const;

export type SocketEvent = keyof typeof socketSchemas;
export type SocketPayload<E extends SocketEvent> = z.infer<(typeof socketSchemas)[E]>;

// ---------------------------------------------------------------------------------------------
// HTTP request bodies
// ---------------------------------------------------------------------------------------------

export const authTelegramSchema = z.object({ initData: z.string().min(10).max(8192) }).strict();

export const onboardingSchema = z
  .object({
    gender: genderSchema,
    adultConfirmed: z.literal(true),
    birthYear: z.number().int().min(1900).max(2100).nullable().optional(),
    displayName: z.string().min(1).max(64).optional(),
    locale: localeSchema.optional(),
  })
  .strict();

export const updateMeSchema = z
  .object({
    displayName: z.string().min(1).max(64),
    locale: localeSchema,
    soundVolume: z.number().int().min(0).max(100),
    musicEnabled: z.boolean(),
    birthYear: z.number().int().min(1900).max(2100).nullable(),
    /** Limited by a cooldown, and not while seated at a table. */
    gender: genderSchema,
    friendsVisibility: z.enum(['everyone', 'me']),
  })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'nothing to update');

export const reportSchema = z
  .object({
    reason: z.enum(['SPAM', 'ABUSE', 'SEXUAL_CONTENT', 'UNDERAGE', 'OTHER']),
    comment: z.string().max(300).optional(),
    /** Optional pointer to what is being reported (a chat message id). */
    chatMessageId: uuid.optional(),
  })
  .strict();

export const admireSchema = z
  .object({ price: z.number().int().positive().max(100_000), clientTxId: clientId })
  .strict();

export const stickerBuySchema = z.object({ pack: z.string().regex(/^[a-z0-9_]{2,32}$/) }).strict();
export const boosterUseSchema = z
  .object({ booster: z.enum(['x2', 'limit', 'bonus']), clientTxId: clientId })
  .strict();
export const activityDaySchema = z.object({ day: z.enum(['today', 'yesterday']).default('today') }).strict();
export const chatTranslateSchema = z.object({ messageId: uuid }).strict();
export const wheelSpinSchema = z.object({ clientTxId: clientId }).strict();
export const chanceBuySchema = z
  .object({ pack: z.string().regex(/^chances_\d{1,4}$/), clientTxId: clientId })
  .strict();
export const vipBuySchema = z.object({ plan: z.string().max(32), clientTxId: clientId }).strict();
export const frameBuySchema = z.object({ code: z.string().regex(/^[a-z0-9_]{2,32}$/) }).strict();
export const frameEquipSchema = z
  .object({
    code: z
      .string()
      .regex(/^[a-z0-9_]{2,32}$/)
      .nullable(),
  })
  .strict();

/** Buying a heart pack with GM in the game ("Buy hearts"). */
export const heartPackBuySchema = z
  .object({ packCode: z.string().regex(/^hearts_\d{1,6}$/), clientTxId: clientId })
  .strict();

/** The shop's "Send" tiles: a fixed amount of GM or hearts to a friend. */
export const transferSchema = z
  .object({ currency: z.enum(['GM', 'HEARTS']), toUserId: uuid, clientTxId: clientId })
  .strict();

/** The portal's status line; an empty text clears it. */
export const statusTextSchema = z.object({ text: z.string().max(140) }).strict();

/** Sets the password for the web portal; `current` is required once a password exists. */
export const setPasswordSchema = z
  .object({ current: z.string().min(1).max(128).optional(), password: z.string().min(6).max(128) })
  .strict();

/** Web portal login outside Telegram: the game id or the Telegram @username, and the password. */
export const passwordLoginSchema = z
  .object({ login: z.string().min(1).max(64), password: z.string().min(1).max(128) })
  .strict();

export const ratingBoardSchema = z.enum(['kisses', 'dj', 'expensive', 'important', 'emotional']);
export const ratingPeriodSchema = z.enum(['day', 'week', 'month', 'all']);

export const cursorQuerySchema = z.object({
  cursor: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const devInitDataSchema = z
  .object({
    telegramId: z
      .number()
      .int()
      .positive()
      .max(2 ** 52),
    firstName: z.string().min(1).max(64),
    username: z
      .string()
      .regex(/^[A-Za-z0-9_]{3,32}$/)
      .optional(),
    languageCode: z.string().max(8).optional(),
    startParam: z.string().max(64).optional(),
  })
  .strict();

export type OnboardingInput = z.infer<typeof onboardingSchema>;
export type UpdateMeInput = z.infer<typeof updateMeSchema>;
export type ReportInput = z.infer<typeof reportSchema>;
export type RatingBoard = z.infer<typeof ratingBoardSchema>;
export type RatingPeriod = z.infer<typeof ratingPeriodSchema>;

export const liveStartSchema = z.object({ title: z.string().trim().min(1).max(80) }).strict();
export const liveGiftSchema = z.object({ giftTypeId: uuid, clientTxId: clientId }).strict();
export const liveReportSchema = z.object({ reason: z.string().trim().max(200).optional() }).strict();
